import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import authProvider from '../authProvider';

interface User {
  id: string | number;
  username?: string;
  email?: string;
  fullName?: string;
  name?: string;
  roles?: string[];
}

interface UserState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasAttemptedFetch: boolean;
  fetchUser: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  resetFetchAttempt: () => void;
}

const store = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      isAuthenticated: false,
      hasAttemptedFetch: false, // Track if we've already attempted to fetch

      fetchUser: async () => {
        const state = get();

        // Prevent multiple simultaneous calls
        if (state.isLoading) {
          return;
        }

        // If we've already attempted to fetch and failed, don't retry immediately
        if (state.hasAttemptedFetch && !state.isAuthenticated) {
          return;
        }

        try {
          set({ isLoading: true });

          // Since we're not using React Admin anymore, skip the getIdentity call
          // and go straight to token-based authentication
          console.log('Checking for token in localStorage...');
          console.log('localStorage keys:', Object.keys(localStorage));

          const token =
            localStorage.getItem('auth.accessToken') ||
            sessionStorage.getItem('auth.accessToken') ||
            localStorage.getItem('access_token') ||
            sessionStorage.getItem('access_token') ||
            localStorage.getItem('token') ||
            sessionStorage.getItem('token');

          console.log('Token found:', token ? 'Yes' : 'No');

          if (token) {
            console.log('Token found, fetching user info from API');

            try {
              // Use the same auth client that authProvider uses
              const { auth } = authProvider;

              // Check if token is expired and refresh if needed
              const isTokenExpired = await auth.isTokenExpire();
              if (isTokenExpired) {
                console.log('Token expired, attempting refresh...');
                try {
                  await auth.fetchRefreshToken();
                  console.log('Token refresh successful');
                } catch (refreshError) {
                  console.error('Token refresh failed:', refreshError);
                  // If refresh fails, clear auth state
                  set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    hasAttemptedFetch: true,
                  });
                  return;
                }
              }

              const profile = await auth.getUserProfile();
              console.log('User profile from auth client:', profile);

              if (profile) {
                const user: User = {
                  id: profile.id || 'api-user',
                  fullName: profile.firstName || profile.email || 'User',
                  roles: profile.roles || ['staff', 'storeManager'],
                };

                console.log('Created user from auth client:', user);
                set({
                  user,
                  isAuthenticated: true,
                  isLoading: false,
                  hasAttemptedFetch: true,
                });

                // Send token to Chrome extension if available
                try {
                  const currentToken = await auth.getAccessToken();
                  if (currentToken) {
                    // Send token to extension via postMessage (content script will pick it up)
                    window.postMessage(
                      {
                        type: 'NEKO_EXTENSION_TOKEN_UPDATE',
                        token: currentToken,
                      },
                      window.location.origin
                    );
                    console.log('[POS Dashboard] Token sent to extension');
                  }
                } catch (tokenError) {
                  console.warn('[POS Dashboard] Could not send token to extension:', tokenError);
                }
              } else {
                throw new Error('No profile returned from auth client');
              }
            } catch (error) {
              console.error('Error fetching user from auth client:', error);
              // Fallback to localStorage data if API fails
              const email = localStorage.getItem('auth.email') || '';
              const username = localStorage.getItem('auth.username') || '';

              const user: User = {
                id: 'fallback-user',
                fullName: username || email || 'User',
                roles: ['staff', 'storeManager'],
              };

              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                hasAttemptedFetch: true,
              });
            }
          } else {
            console.log('No token found, user not authenticated');
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              hasAttemptedFetch: true,
            });
          }
        } catch (error) {
          console.error('Failed to fetch user:', error);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasAttemptedFetch: true,
          });
        }
      },

      logout: async () => {
        try {
          await authProvider.logout({});
          set({
            user: null,
            isAuthenticated: false,
          });
          // Redirect to login
          window.location.href = '/login';
        } catch (error) {
          console.error('Logout failed:', error);
          // Force redirect anyway
          window.location.href = '/login';
        }
      },

      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      resetFetchAttempt: () => {
        set({
          hasAttemptedFetch: false,
        });
      },
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        hasAttemptedFetch: state.hasAttemptedFetch,
      }),
    }
  )
);

// Export the store as the default hook
export const useUserStore = store;

// Expose the store to the window object for testing
if (typeof window !== 'undefined') {
  (window as any).userStore = store;
}
